import {
  formatarIdentificacaoObra,
  identificacaoDaObra,
  type IdentificacaoObra,
} from "@/shared/utils/formatar-identificacao-obra";
import {
  calcularConflitos,
  formatDayMonth,
  formatDurationShort,
  isObraCancelada,
  isPast,
  positionInterval,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  ConflitoAlocacao,
  GanttIndicators,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import { toGanttObra } from "@/modules/recursos";
import type { ObraAlocacoes, ObraTipo } from "@/modules/recursos";
import {
  obraCardTitle,
  rangeLine,
  toConflictInfo,
} from "@/shared/components/gantt-carga/gantt-rows";
import type {
  GanttBand,
  GanttItem,
  GanttPill,
  GanttRowModel,
} from "@/shared/components/gantt-carga/gantt-rows";

/** Escopo macro ausente é mostrado como "-". */
export const ESCOPO_VAZIO = "-";

/**
 * Pílula do escopo macro dentro da linha da obra: o escopo é recortado ao período visível e à linha,
 * e convertido para % da largura da linha.
 */
function buildPill(
  line: { left: number; width: number },
  escopo: { inicio: IsoDate; fim: IsoDate },
  timeline: Timeline,
): GanttPill | undefined {
  const pos = positionInterval(escopo, timeline);
  if (!pos || line.width <= 0) return undefined;
  const from = Math.max(pos.left, line.left);
  const to = Math.min(pos.left + pos.width, line.left + line.width);
  if (to <= from) return undefined;
  return {
    left: ((from - line.left) / line.width) * 100,
    width: ((to - from) / line.width) * 100,
    label: formatDurationShort(escopo),
  };
}

/**
 * Linha da visão por Obras: uma por escopo macro da obra x alocação (fornecedor). A primeira de cada obra
 * carrega a identificação dela; as demais deixam a coluna da obra em branco.
 */
/** Sem `conflitos`/`profissional`: só a Carga de Profissionais resolve conflito (HU 2434). */
export interface ObraAlocacaoRow extends Omit<GanttRowModel, "conflitos" | "profissional"> {
  /** `title` é o nome do fornecedor; a obra e o escopo ficam nas colunas ao lado. */
  obra: { codigo: string; nome: string; rotulo: IdentificacaoObra; tipos: ObraTipo[] };
  escopoMacro: string;
  /** Período da linha fina: o da obra; sem datas da obra, o da alocação. */
  periodo: { inicio: IsoDate; fim: IsoDate };
  firstOfObra: boolean;
  /** Fornecedor da linha (mesmo id de `CargaTrabalhoProfissional`): insumo de "Resolver Conflito". */
  fornecedorId: string;
}

/**
 * Visão por Obras da Carga de Trabalho de Fornecedores (US-1774): colunas Obra | Escopo Macro |
 * Fornecedor, uma linha por escopo macro x alocação. Os conflitos continuam sendo do fornecedor: são
 * calculados sobre todas as obras devolvidas (canceladas ignoradas), e cada linha mostra os
 * pares que envolvem a sua alocação.
 */
export function buildFornecedorObraRows(
  obras: ObraAlocacoes[],
  timeline: Timeline,
  indicators: GanttIndicators,
  today: IsoDate,
): ObraAlocacaoRow[] {
  const porFornecedor = new Map<string, CargaTrabalhoAlocacao[]>();
  const fornecedorDe = new Map<string, string>();
  for (const obra of obras) {
    const ganttObra = toGanttObra(obra);
    // Mesmo período que a barra desenha (obra; cai na alocação se faltar), para a faixa de conflito alinhar com ela.
    const temPeriodoObra = Boolean(obra.obraInicio && obra.obraFim);
    for (const alocacao of obra.alocacoes) {
      const key = alocacao.fornecedor.id || alocacao.fornecedor.nome;
      const lista = porFornecedor.get(key) ?? [];
      lista.push({
        id: alocacao.id,
        papelObraId: null,
        inicio: temPeriodoObra ? (obra.obraInicio as IsoDate) : alocacao.inicio,
        fim: temPeriodoObra ? (obra.obraFim as IsoDate) : alocacao.fim,
        obra: ganttObra,
      });
      porFornecedor.set(key, lista);
      fornecedorDe.set(alocacao.id, alocacao.fornecedor.nome);
    }
  }
  const conflitos = [...porFornecedor.values()].flatMap((lista) => calcularConflitos(lista));

  return obras.flatMap((obra) => {
    const cancelada = isObraCancelada(obra);
    const identificacao = {
      codigo: obra.codigo,
      nome: obra.nome,
      rotulo: identificacaoDaObra(obra),
      tipos: obra.tipos ?? [],
    };
    const visivel = indicators[cancelada ? "cancelada" : "obra"];

    if (obra.alocacoes.length === 0) {
      return [
        {
          key: `${obra.id}-vazia`,
          kind: "profissional" as const,
          title: ESCOPO_VAZIO,
          subtitle: null,
          meta: null,
          conflicts: 0,
          conflictTooltip: "",
          items: [],
          bands: [],
          lanes: 1,
          obra: identificacao,
          escopoMacro: ESCOPO_VAZIO,
          periodo: { inicio: obra.obraInicio ?? "", fim: obra.obraFim ?? "" },
          firstOfObra: true,
          fornecedorId: "",
        },
      ];
    }

    // Uma linha por escopo macro da obra x alocação; as datas são as da alocação. Sem escopos, uma linha por alocação.
    const ordenadas = [...obra.alocacoes].sort(
      (a, b) => a.inicio.localeCompare(b.inicio) || a.fim.localeCompare(b.fim),
    );

    const escopos = [...(obra.escoposMacro ?? [])].sort(
      (a, b) => a.inicio.localeCompare(b.inicio) || a.fim.localeCompare(b.fim),
    );
    const opcoes =
      escopos.length > 0
        ? escopos.map((e) => ({
            escopo: e.escopo.trim() || ESCOPO_VAZIO,
            id: e.id,
            inicio: e.inicio,
            fim: e.fim,
          }))
        : [{ escopo: ESCOPO_VAZIO, id: "", inicio: "", fim: "" }];
    const combinacoes = opcoes.flatMap((escopo) =>
      ordenadas.map((alocacao) => ({ alocacao, escopo })),
    );

    return combinacoes.map(({ alocacao, escopo }, index): ObraAlocacaoRow => {
      const rowKey = escopo.id ? `${alocacao.id}-${escopo.id}` : alocacao.id;
      const escopoMacro = escopo.escopo;
      // Linha fina = período da obra (cai na alocação se a obra não trouxer as datas).
      const periodo =
        obra.obraInicio && obra.obraFim
          ? { inicio: obra.obraInicio, fim: obra.obraFim }
          : { inicio: alocacao.inicio, fim: alocacao.fim };
      const position = visivel ? positionInterval(periodo, timeline) : null;
      const escopoPeriodo = escopo.id ? { inicio: escopo.inicio, fim: escopo.fim } : periodo;
      // Sem escopo macro não há o que medir: a linha da obra fica sem a pílula de duração.
      const pill = position && escopo.id ? buildPill(position, escopoPeriodo, timeline) : undefined;
      const items: GanttItem[] = position
        ? [
            {
              key: rowKey,
              kind: "bar",
              lane: 0,
              left: position.left,
              width: position.width,
              label: alocacao.fornecedor.nome,
              start: formatDayMonth(periodo.inicio),
              end: formatDayMonth(periodo.fim),
              duration: pill?.label,
              pill,
              past: !cancelada && isPast(periodo, today),
              cancelada,
              segments: [{ kind: cancelada ? "cancelada" : "obra", left: 0, width: 100 }],
              tooltip: [
                `Fornecedor ${alocacao.fornecedor.nome}`,
                obraCardTitle(obra),
                ...(escopoMacro === ESCOPO_VAZIO ? [] : [`Escopo Macro: ${escopoMacro}`]),
                ...(cancelada ? ["Situação: Cancelada"] : []),
                ...(escopo.id ? [rangeLine("Escopo Macro", escopoPeriodo)] : []),
                rangeLine("Alocação", alocacao),
              ].join("\n"),
            },
          ]
        : [];

      const daLinha = conflitos.filter(
        (conflito: ConflitoAlocacao) =>
          conflito.a.id === alocacao.id || conflito.b.id === alocacao.id,
      );
      const bands: GanttBand[] = [];
      const lines: string[] = [];
      daLinha.forEach((conflito, position) => {
        const detail = [
          `Fornecedor ${fornecedorDe.get(conflito.a.id) ?? ""}`,
          `Obra ${formatarIdentificacaoObra(conflito.a.obra)}`,
          `Obra ${formatarIdentificacaoObra(conflito.b.obra)}`,
          rangeLine("Sobreposição", conflito),
        ].join("\n");
        lines.push(detail);
        const faixa = positionInterval(conflito, timeline);
        if (faixa) {
          bands.push({
            key: `${rowKey}-conflito-${position}`,
            left: faixa.left,
            width: faixa.width,
            tooltip: `Conflito detectado\n${detail}`,
            info: toConflictInfo(conflito),
          });
        }
      });

      return {
        key: rowKey,
        kind: "profissional",
        title: alocacao.fornecedor.nome || ESCOPO_VAZIO,
        subtitle: null,
        meta: null,
        conflicts: daLinha.length,
        conflictTooltip: lines.length > 0 ? `Conflito detectado\n${lines.join("\n\n")}` : "",
        conflictInfos: daLinha.map(toConflictInfo),
        items,
        bands,
        lanes: 1,
        obra: identificacao,
        escopoMacro,
        periodo,
        firstOfObra: index === 0,
        fornecedorId: alocacao.fornecedor.id || alocacao.fornecedor.nome,
      };
    });
  });
}
